import { getLuminance } from 'polished'
import type { BannerKind } from 'LuxKit/Banners/types'
import { removeRegionCode } from 'lib/string/regionUtils'

const DEFAULT_HEADER_BANNER_BACKGROUND = '#CAFFEB'
const DEFAULT_HEADER_BANNER_TEXT_COLOR = '#363A45'
const DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR = '#FFFFFF'
const DEFAULT_HEADER_BANNER_KIND: App.LabHeaderBannerResult['kind'] = 'promotional-primary'
const MIDDLE_GREY_LUMINANCE = getLuminance('#777')

export type TakeoverBannerLocation = 'search' | 'offer' | 'generic'

export const getTakeoverBannerLocation = (
  isSearchPage: boolean,
  isOfferPage: boolean,
): TakeoverBannerLocation => {
  if (isSearchPage) {
    return 'search'
  }
  if (isOfferPage) {
    return 'offer'
  }
  return 'generic'
}

export const getRenderableHeaderBanner = (
  headerBanners: App.LabHeaderBannersResult | undefined,
  dismissedIds: Set<string>,
): App.LabHeaderBannerResult | undefined => {
  if (!headerBanners?.banners?.length) {
    return undefined
  }

  const sorted = headerBanners.banners.slice().sort((a, b) => a.rank - b.rank)
  return sorted.find(banner => !dismissedIds.has(banner.id))
}

export interface LuxPlusRegionOverride {
  isOutsideMemberRegion: boolean
  memberRegion: string
  switchRegionLabel: string
}

export const getHeaderBannerPresentation = (
  banner: App.LabHeaderBannerResult,
  isMobile: boolean,
  luxPlusRegionOverride?: LuxPlusRegionOverride,
): {
  kind: BannerKind
  isCustomAppearance: boolean
  description: string
  cta?: { label: string, path: string, style: 'primary' | 'secondary' | 'tertiary' }
  isLuxPlusRegionSwitch: boolean
  background: { mode: 'solid' | 'gradient', value: string } | { mode: 'image', imageId: string }
  textColor: string
  ctaPrimaryTextColor: string
  iconImageId?: string
} => {
  const description = isMobile && banner.description.mobile ? banner.description.mobile : banner.description.desktop
  const ctaItem = isMobile
    ? (banner.cta?.mobile ?? banner.cta?.desktop)
    : banner.cta?.desktop
  let cta = ctaItem
    ? {
        ...ctaItem,
        style: ctaItem.style ?? ((ctaItem as { outlined?: boolean }).outlined ? 'secondary' : 'tertiary'),
      }
    : undefined

  const isLuxPlusRegionSwitch = !!banner.isLuxPlusBanner &&
    !!luxPlusRegionOverride?.isOutsideMemberRegion &&
    !!cta

  if (isLuxPlusRegionSwitch && cta) {
    cta = {
      ...cta,
      label: luxPlusRegionOverride.switchRegionLabel,
      path: `/${luxPlusRegionOverride.memberRegion}${removeRegionCode(cta.path, luxPlusRegionOverride.memberRegion)}`,
    }
  }

  const textColor = banner.textColor || DEFAULT_HEADER_BANNER_TEXT_COLOR
  const ctaPrimaryTextColor = banner.ctaPrimaryTextColor || DEFAULT_HEADER_BANNER_CTA_PRIMARY_TEXT_COLOR

  const background = banner.background.mode === 'image'
    ? {
        mode: 'image' as const,
        imageId: banner.background.imageId,
      }
    : {
        mode: banner.background.mode,
        value: banner.background.value || DEFAULT_HEADER_BANNER_BACKGROUND,
      }

  return {
    kind: banner.isCustomAppearance ?
        (getLuminance(textColor) > MIDDLE_GREY_LUMINANCE ? 'none-inverse' : 'none') :
      banner.kind || DEFAULT_HEADER_BANNER_KIND,
    isCustomAppearance: banner.isCustomAppearance ?? false,
    description,
    cta,
    isLuxPlusRegionSwitch,
    background,
    textColor,
    ctaPrimaryTextColor,
    iconImageId: banner.iconImageId?.trim() || undefined,
  }
}
